// src/pages/CourseTime/components/slides/AssessmentSlide.comp.jsx
import React, { useMemo } from 'react';
import styled from 'styled-components';
import ReactHtmlParser from 'react-html-parser';
import { useGlobalState } from '../../../../context/global_state';
import { getActiveThemeColor } from '../../../../utils/utils';
import { GET_APP_LOCALIZE_TEXT } from '../../../../utils/dq_lc_service/lc_service';
import RazProgressSVG from '../../../../components/UI/SVG_IMGs/RazProgressSVG';
import { useAssessmentTooltips } from '../../hooks/useAssessmentTooltips';
import ChoiceQuestion from './assessment/ChoiceQuestion.comp';
import FreeTextQuestion from './assessment/FreeTextQuestion.comp';
import OrderingQuestion from './assessment/OrderingQuestion.comp';
import '../../../../assets/css/glossaryTerm.css';

// Styled components
const AssessmentContainer = styled.div`
  width: 100%;
  flex: 1;
  padding: 20px;
  font-family: "Source Sans Pro", "Helvetica Neue", sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 20px 30px;
  }
`;

const QuestionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;

const SubtitleTop = styled.div`
  display: inline-block;
  color: ${props => props.themeColor || '#fe696e'};
  background-color: #f1f6fd;
  font-weight: 700;
  text-align: center;
  margin-bottom: 12px;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 20px;

  a {
    color: ${props => props.themeColor || '#fe696e'};
    text-decoration: underline;
  }
`;

// Header row: "QUESTION 02 OF 45" block + divider + question title, with a circular
// progress ring on the right. Desktop sizes are the base rules; max-width:1024px
// (iPad) and max-width:768px (mobile) tiers scale things down. On mobile the row
// reflows to 2 lines via flex-wrap + order: number block + ring share the top row,
// question text drops to a full-width row below (same pattern used in QuizTime's
// QuestionPoster header row).
const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
  gap: 24px;

  @media (max-width: 1024px) {
    gap: 16px;
  }

  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-shrink: 0;

  @media (max-width: 1024px) {
    gap: 14px;
  }

  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const QuestionNumberCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const QuestionLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #9aa0a6;
  text-transform: uppercase;

  @media (max-width: 1024px) {
    font-size: 11px;
  }

  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const QuestionBigNumber = styled.div`
  font-size: 40px;
  font-weight: 800;
  color: ${props => props.themeColor || '#fe696e'};
  line-height: 1.15;

  @media (max-width: 1024px) {
    font-size: 32px;
  }

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const QuestionOfTotal = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #9aa0a6;
  letter-spacing: 0.5px;

  @media (max-width: 1024px) {
    font-size: 11px;
  }

  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const HeaderDivider = styled.div`
  width: 2px;
  align-self: stretch;
  background-color: #e5e7eb;
  flex-shrink: 0;
`;

const HeaderQuestionText = styled.div`
  color: #2b2f36;
  font-weight: 700;
  font-size: 24px;
  line-height: 1.35;
  text-align: left;
  flex: 1;
  min-width: 0;

  @media (max-width: 1024px) {
    font-size: 22px;
  }

  @media (max-width: 768px) {
    font-size: 20px;
    order: 3;
    flex: 1 1 100%;
    margin-top: 4px;
  }
`;

const ProgressRingCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const ProgressRingWrapper = styled.div`
  position: relative;
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 1024px) {
    width: 60px;
    height: 60px;
  }

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
  }
`;

const ProgressRingBackground = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const ProgressRingPercent = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${props => props.themeColor || '#fe696e'};

  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const SubtitleBottom = styled.div`
  color: #666666;
  font-weight: 700;
  text-align: center;
  margin-top: 10px;
  margin-bottom: 20px;
  font-size: 20px;
`;

const OptionsContainer = styled.div`
  margin-top: 30px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  position: relative;
`;

const InfoText = styled.p`
  text-align: center;
  color: #666;
  font-size: 14px;
  margin-top: 20px;
  font-style: italic;
`;

const NavigationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 40px;
`;

const NavButtonPrev = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  border-radius: 999px;
  border: 1.5px solid #e3e5e8;
  background-color: transparent;
  color: #9aa0a6;
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #c7cbd1;
    color: #6c737f;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NavButtonNext = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  border-radius: 999px;
  border: none;
  background-color: ${props => props.themeColor || '#fe696e'};
  color: #ffffff;
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const TooltipContainer = styled.div`
  position: fixed;
  background-color: ${props => props.backgroundColor || '#fe696e'};
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  max-width: 300px;
  z-index: 1000;
  pointer-events: none;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

// Shell around a single assessment question: subtitles, the "QUESTION X OF N"
// header row + progress ring, the tooltip overlay and the prev/next navigation.
// The answer UI itself is delegated by question type to
// OrderingQuestion (MTQ) / FreeTextQuestion (OPQ) / ChoiceQuestion (MCQ, MAQ).
const AssessmentSlide = ({
  slide,
  renderSpokenText,
  // Activity props
  activityCompleted,
  interactionLoading,
  fetchingExistingRecord,
  inputsDisabled,
  onAssessmentSubmit,
  onEditActivity,
  assessmentAnswers,
  setAssessmentAnswers,
  questionNumber,
  totalQuestions,
  pageLc,
  onNextSlide,
  isNextButtonDisabled,
  onPrevSlide,
  isPrevButtonDisabled
}) => {
  const { profile } = useGlobalState();
  const themeColor = getActiveThemeColor(profile);

  // Use the assessment answers from the activity hook instead of local state
  const answersMap = assessmentAnswers || {};
  const setAnswersMap = setAssessmentAnswers || (() => {});

  const assessment = slide.SlideContent || {};
  const assessmentType = slide.assessment_type; // 'pre' or 'post'
  const slideId = slide.SlideID;

  // Extract assessment data
  const questionType = assessment.category_11 || 'MCQ';
  const questionText = assessment.instrument_question || slide.SlideTitle || '';
  const subtitleTop = assessment.question_subtitle_top || '';
  const subtitleBottom = assessment.question_subtitle_bottom || '';
  const uiRules = assessment.ui_rules || [];

  // Parse options
  const options  = useMemo(() => {
  try {
      const value = typeof assessment.instrument_response === 'string'
        ? JSON.parse(assessment.instrument_response)
        : assessment.instrument_response;
      if (!Array.isArray(value)) return [value];

      return value;
    } catch (e) {
      console.error('Error parsing assessment options:', e);
      return [];
    }
  }, [assessment]);

  // ui_rules processing (tooltip keyword wrapping, USER_INPUT/UNSELECT indices)
  // and the hover-tooltip state.
  const {
    processedChoices,
    inputQuestions,
    unselectQuestions,
    tooltipText,
    displayTooltip,
    tooltipPos
  } = useAssessmentTooltips({ assessment, slide, options });

  // Check if it's a free text question
  const isFreeText = questionType === 'OPQ' && options.includes('Free text');
  const isMultiSelect = questionType === 'MAQ';
  const isMTQ = questionType === 'MTQ';

  // Get current answer for this question from the map
  const currentAnswer = answersMap[slideId];

  // Question progress ("QUESTION 02 OF 45" + ring percentage)
  const hasQuestionMeta = Number.isFinite(totalQuestions) && totalQuestions > 0 && Number.isFinite(questionNumber) && questionNumber > 0;
  const progressPercent = hasQuestionMeta ? Math.round((questionNumber / totalQuestions) * 100) : 0;
  const ringRadius = 28;
  const ringCircumference = 2 * Math.PI * ringRadius;

  return (
    <AssessmentContainer style={{ position: 'relative' }}>
      {/* Loading overlay */}
      {(interactionLoading || fetchingExistingRecord) && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          pointerEvents: 'all'
        }}>
          <div style={{ textAlign: 'center' }}>
            <i className="fa fa-spinner fa-spin" style={{ fontSize: '48px', color: themeColor }} />
            <p style={{ marginTop: '16px', fontSize: '16px', fontWeight: 600, color: '#666' }}>
              {fetchingExistingRecord ? GET_APP_LOCALIZE_TEXT(pageLc, 'ct__loading_existing_record') + '...' : GET_APP_LOCALIZE_TEXT(pageLc, 'ct__submitting') + '...'}
            </p>
          </div>
        </div>
      )}

      {/* Tooltip */}
      <TooltipContainer
        visible={displayTooltip}
        backgroundColor={themeColor}
        style={{
          top: tooltipPos.y,
          left: tooltipPos.x
        }}
      >
        {ReactHtmlParser(tooltipText)}
      </TooltipContainer>

      <QuestionContainer>
        {/* Subtitle Top */}
        {subtitleTop && (
          <SubtitleTop themeColor={themeColor}>
            <span>{renderSpokenText ? renderSpokenText('subtitle_top', subtitleTop) : subtitleTop}</span>
          </SubtitleTop>
        )}

        {/* Header: QUESTION X OF N | divider | question title ... progress ring */}
        <HeaderRow>
          <HeaderLeft>
            {hasQuestionMeta && (
              <>
                <QuestionNumberCol>
                  <QuestionLabel>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__question')}</QuestionLabel>
                  <QuestionBigNumber themeColor={themeColor}>
                    {String(questionNumber).padStart(2, '0')}
                  </QuestionBigNumber>
                  <QuestionOfTotal>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__of')} {totalQuestions}</QuestionOfTotal>
                </QuestionNumberCol>
                <HeaderDivider />
              </>
            )}
          </HeaderLeft>

          <HeaderQuestionText>
            <span>{renderSpokenText ? renderSpokenText('question', questionText) : questionText}</span>
            {isMultiSelect && (
              <InfoText style={{ textAlign: 'left', fontWeight: 700, fontStyle: 'normal', margin: '8px 0 0' }}>
                {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__select_multiple_answers')}
              </InfoText>
            )}
            {isFreeText && (
              <InfoText style={{ textAlign: 'left', fontWeight: 700, fontStyle: 'normal', margin: '8px 0 0' }}>
                {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__assessment_response_note')}
              </InfoText>
            )}
          </HeaderQuestionText>

          {hasQuestionMeta && (
            <ProgressRingCol>
              <ProgressRingWrapper>
                <ProgressRingBackground viewBox="0 0 64 64">
                  <circle cx="32" cy="32" r={ringRadius} fill="none" stroke="#e5e7eb" strokeWidth="3" />
                  <circle
                    cx="32"
                    cy="32"
                    r={ringRadius}
                    fill="none"
                    stroke={themeColor}
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray={ringCircumference}
                    strokeDashoffset={ringCircumference * (1 - progressPercent / 100)}
                    transform="rotate(-90 32 32)"
                  />
                </ProgressRingBackground>
                <RazProgressSVG color={themeColor} style={{ width: 36, height: 36, position: 'relative' }} />
              </ProgressRingWrapper>
              <ProgressRingPercent themeColor={themeColor}>{progressPercent}%</ProgressRingPercent>
            </ProgressRingCol>
          )}
        </HeaderRow>

        {/* Subtitle Bottom */}
        {subtitleBottom && (
          <SubtitleBottom themeColor={themeColor}>
            <span>{renderSpokenText ? renderSpokenText('subtitle_bottom', subtitleBottom) : subtitleBottom}</span>
          </SubtitleBottom>
        )}

        {/* Options or Free Text */}
        <OptionsContainer>
          {isMTQ ? (
            <OrderingQuestion
              processedChoices={processedChoices}
              slideId={slideId}
              currentAnswer={currentAnswer}
              setAnswersMap={setAnswersMap}
              themeColor={themeColor}
              renderSpokenText={renderSpokenText}
              pageLc={pageLc}
            />
          ) : isFreeText ? (
            <FreeTextQuestion
              slideId={slideId}
              currentAnswer={currentAnswer}
              setAnswersMap={setAnswersMap}
              themeColor={themeColor}
              pageLc={pageLc}
            />
          ) : (
            <ChoiceQuestion
              processedChoices={processedChoices}
              inputQuestions={inputQuestions}
              unselectQuestions={unselectQuestions}
              uiRules={uiRules}
              isMultiSelect={isMultiSelect}
              slideId={slideId}
              currentAnswer={currentAnswer}
              setAnswersMap={setAnswersMap}
              themeColor={themeColor}
              renderSpokenText={renderSpokenText}
            />
          )}
        </OptionsContainer>

        {/* Navigation Buttons matching design */}
        <NavigationRow>
          {onPrevSlide ? (
            <NavButtonPrev
              onClick={onPrevSlide}
              disabled={isPrevButtonDisabled}
            >
              ‹ Previous
            </NavButtonPrev>
          ) : <div />}
          {onNextSlide && (
            <NavButtonNext
              themeColor={themeColor}
              onClick={onNextSlide}
              disabled={isNextButtonDisabled}
            >
              Next ›
            </NavButtonNext>
          )}
        </NavigationRow>

      </QuestionContainer>
    </AssessmentContainer>
  );
};

export default AssessmentSlide;
